import { type ReactNode, useEffect, useRef, useState } from "react";
import type { RiskResult } from "@gram/client/models/components/riskresult.js";
import { cn } from "@/lib/utils";
import {
  isLlmAnalyzerRuleId,
  ruleIdCategoryLabel,
} from "@/pages/security/rule-ids";
import {
  getCategoryCodeForFinding,
  getRuleTitleFallback,
  isJudgeSource,
  isLlmAnalyzerSource,
  JUDGE_SOURCES,
  LLM_ANALYZER_SOURCE,
} from "@/pages/security/risk-utils";
import { useRevealAll } from "@/pages/security/reveal-all-context";

/** Soft warning (yellow) wash for a *non-sensitive* flagged span — always shown,
 * so it keeps the natural proportional text. */
const RISK_MARK_CLASS =
  "bg-warning-softest px-0.5 text-foreground ring-1 ring-warning-softest";

/** A sensitive span toggles between dotted-out and revealed, so both states must
 * occupy the exact same width or revealing reflows the message. Shared box +
 * monospace (so char-count == width); red tint matching the risk divider, with a
 * stronger wash once revealed to signal the value is exposed. */
const SENSITIVE_MARK_BASE = "px-0.5 font-mono ring-1";
const SENSITIVE_MARK_MASKED = "bg-red-700/10 text-red-700 ring-red-700/20";
const SENSITIVE_MARK_REVEALED = "bg-red-700/15 text-red-700 ring-red-700/30";

export function getRiskBadgeLabel(result: RiskResult): string {
  if (result.ruleId === "llm_judge") return getRuleTitleFallback(result.ruleId);
  return (
    ruleIdCategoryLabel(result.ruleId) ||
    getCategoryCodeForFinding(result.source, result.ruleId)
  );
}

/** Judge findings carry one rule per category, so their rule id only restates
 * the badge beside it ("prompt_injection" under PROMPT_INJECTION). The LLM
 * analyzer's do the same ("secret.llm" under SECRET) and would name the engine
 * on top, so they stay hidden too. */
export function shouldShowRiskRuleId(result: RiskResult): boolean {
  return (
    Boolean(result.ruleId) &&
    !isJudgeSource(result.source) &&
    !isLlmAnalyzerSource(result.source)
  );
}

/** The short label a tool section shows beside a highlighted match: the rule
 * id for scanner findings (it is what an exclusion expression names), the
 * source for a judge verdict whose constant rule id adds nothing, and the
 * rule's label for the LLM analyzer, whose ids name the engine. */
export function sectionRiskLabel(result: RiskResult): string {
  if (!result.ruleId || result.ruleId === "llm_judge") return result.source;
  if (isLlmAnalyzerRuleId(result.ruleId)) {
    return getRuleTitleFallback(result.ruleId);
  }
  return result.ruleId;
}

export function riskResultAnchorId(result: RiskResult): string | undefined {
  return result.chatContentPartId ?? result.chatMessageId;
}

/** Whether one finding flags a literal secret or personal datum: the scanner
 * sources by construction, and the LLM analyzer when its rule id lands in the
 * secret or PII category (its other rules describe behavior, not values). */
function resultIsSensitive(result: RiskResult): boolean {
  if (result.source === "gitleaks" || result.source === "presidio") return true;
  if (!isLlmAnalyzerSource(result.source)) return false;
  const ruleId = result.ruleId ?? "";
  return ruleId.startsWith("secret.") || ruleId.startsWith("pii.");
}

/** A finding from gitleaks/presidio carries a literal secret; its match is
 * masked until the viewer explicitly reveals it. */
export function resultsAreSensitive(
  results: RiskResult[] | undefined,
): boolean {
  return results?.some(resultIsSensitive) ?? false;
}

/** A sensitive finding with no span the transcript could dot out on its own:
 * the LLM analyzer reports secrets and personal data without locating them
 * (its match is empty, its rationale may quote the value), so the only thing
 * left to mask is the message as a whole. Scanner findings always carry a
 * span and never qualify. */
export function resultIsSpanlessSensitive(result: RiskResult): boolean {
  return resultIsSensitive(result) && !matchIsMessageContent(result);
}

/** Whether a row must mask its whole body rather than individual spans, see
 * `resultIsSpanlessSensitive`. Until the analyzer emits a redacted span the
 * body is the smallest unit that provably covers the flagged value. */
export function needsWholeMessageMask(
  results: RiskResult[] | undefined,
): boolean {
  return results?.some(resultIsSpanlessSensitive) ?? false;
}

/** Count of distinct findings (by source/rule/match), matching the RiskBadge's
 * grouping — used for the "N risks" turn-divider label. */
export function distinctRiskCount(results: RiskResult[]): number {
  const keys = new Set<string>();
  for (const r of results) {
    keys.add(`${r.source} ${r.ruleId ?? ""} ${r.match ?? ""}`);
  }
  return keys.size;
}

/**
 * Per-source overrides for how a finding's `match` is rendered. By default a
 * match is a span lifted from the message the reviewer is reading, so the UI
 * highlights it inline (or surfaces it as an out-of-text "flagged value" when it
 * was stripped for display) and repeats it as a reveal-gated chip in the
 * findings popover. Some policy types instead match on something that isn't a
 * span of the message and is already stated in the finding description, making
 * those renderings pure redundancy: session/account metadata, or the whole
 * rendered event a judge evaluated.
 *
 * To adjust a policy type's match rendering, add its source here — the two flags
 * are independent, so a future type opts into only what applies to it.
 */
type MatchDisplayOverride = {
  /** The match isn't drawn from the message text: don't highlight it inline or
   * surface it as an out-of-text "flagged value" annotation. */
  notMessageContent?: boolean;
  /** The match already appears in the finding description: skip the reveal-gated
   * chip in the findings popover that would just repeat it. */
  shownInDescription?: boolean;
};

// A judge finding's match is the whole rendered event the judge read, a JSON
// envelope whose `body` field holds the message itself, not a span within it. It
// therefore never appears in the displayed text, and rendering it verbatim
// reprints the message the reviewer is already looking at, wrapped in JSON. The
// judge's rationale (the description) is what actually explains the finding.
const JUDGE_MATCH_DISPLAY: MatchDisplayOverride = {
  notMessageContent: true,
  shownInDescription: true,
};

const MATCH_DISPLAY_OVERRIDES: Record<
  string,
  MatchDisplayOverride | undefined
> = {
  // The account the session authenticated as (an email): stated in the
  // description and shown on the message author chip, never message content.
  account_identity: { notMessageContent: true, shownInDescription: true },
  ...Object.fromEntries(JUDGE_SOURCES.map((s) => [s, JUDGE_MATCH_DISPLAY])),
  // The LLM analyzer reports no spans: its match is empty and its description
  // is the model's reasoning, which may quote the content it flagged. Same
  // rendering as a judge verdict, so nothing tries to locate it in the text.
  [LLM_ANALYZER_SOURCE]: JUDGE_MATCH_DISPLAY,
};

/** Whether a finding's match is a span of the message text — highlighted inline
 * and eligible for the out-of-text "flagged value" annotation. False for
 * metadata matches like the authenticated account email. */
function matchIsMessageContent(result: RiskResult): boolean {
  return !MATCH_DISPLAY_OVERRIDES[result.source]?.notMessageContent;
}

/** Whether a finding's match is already conveyed by its description, so the
 * findings popover should not repeat it as a separate reveal-gated chip. */
export function matchShownInDescription(result: RiskResult): boolean {
  return Boolean(MATCH_DISPLAY_OVERRIDES[result.source]?.shownInDescription);
}

/** Distinct, non-empty match strings to highlight, longest first so a longer
 * secret wins over a substring of it. */
export function getMatchStrings(results: RiskResult[] | undefined): string[] {
  if (!results) return [];
  const set = new Set<string>();
  for (const r of results) {
    if (!matchIsMessageContent(r)) continue;
    if (r.match) set.add(r.match);
  }
  return [...set].sort((a, b) => b.length - a.length);
}

/** `match` as it reads inside a JSON string, e.g. in tool call arguments, where
 * a quote, backslash or newline is escaped. */
export function jsonEscaped(match: string): string {
  return JSON.stringify(match).slice(1, -1);
}

/** `matches` plus each one's JSON-escaped form, longest first. */
export function withJsonEscaped(matches: string[]): string[] {
  const all = new Set(matches);
  for (const match of matches) all.add(jsonEscaped(match));
  return [...all].sort((a, b) => b.length - a.length);
}

/** Secret and PII matches, JSON-escaped too. A message holding any of them has
 * every match masked; otherwise matches are highlighted but readable. */
export function sensitiveMatchStrings(
  results: RiskResult[] | undefined,
): string[] {
  return withJsonEscaped(getMatchStrings(results?.filter(resultIsSensitive)));
}

export function maskValue(value: string): string {
  // Mask character-for-character so revealing/hiding doesn't change the text
  // length (and thus doesn't shift surrounding layout).
  return "•".repeat(value.length);
}

/** `maskValue` for a whole message body: line breaks survive so the masked
 * block keeps the message's shape and revealing it doesn't reflow the row. */
export function maskBlock(text: string): string {
  return text.replace(/[^\n]/g, "•");
}

// Keep a per-row reveal toggle in sync with the panel-wide "reveal all" switch
// without letting the global object clobber a manual per-row click.
export function useRowReveal(sensitive: boolean): {
  revealed: boolean;
  setRevealed: (next: boolean) => void;
} {
  const reveal = useRevealAll();
  const generation = reveal?.generation;
  const revealAll = reveal?.revealAll ?? false;
  const [revealed, setRevealed] = useState(sensitive ? revealAll : true);
  const lastGeneration = useRef(generation);
  const lastSensitive = useRef(sensitive);
  useEffect(() => {
    const becameSensitive = sensitive && !lastSensitive.current;
    const generationChanged =
      generation !== undefined && lastGeneration.current !== generation;
    lastSensitive.current = sensitive;
    if (generation !== undefined) lastGeneration.current = generation;
    // Re-apply the masked (reveal-all) default when the row newly becomes
    // sensitive — e.g. findings arrive after first paint — so it can't stay
    // revealed from its earlier non-sensitive state, and when the panel-wide
    // reveal-all switch flips. Manual per-row toggles between those are kept.
    if (sensitive && (becameSensitive || generationChanged)) {
      setRevealed(revealAll);
    }
  }, [sensitive, generation, revealAll]);
  return { revealed, setRevealed };
}

/** The merged, sorted `[start, end)` character ranges covering every occurrence
 * of `matches` in `text`. Overlapping/adjacent occurrences are coalesced so a
 * longer secret and a substring of it don't double-mark. */
export function matchRanges(
  text: string,
  matches: string[],
): Array<[number, number]> {
  const ranges: Array<[number, number]> = [];
  for (const match of matches) {
    if (!match) continue;
    let from = 0;
    let idx = text.indexOf(match, from);
    while (idx !== -1) {
      ranges.push([idx, idx + match.length]);
      from = idx + match.length;
      idx = text.indexOf(match, from);
    }
  }
  if (ranges.length === 0) return ranges;

  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) {
      last[1] = Math.max(last[1], range[1]);
    } else {
      merged.push([range[0], range[1]]);
    }
  }
  return merged;
}

/** A window of message text kept around one or more flagged spans when the rest
 * of a long message is collapsed away. */
export interface MessageSnippet {
  /** The slice of the original message to render (spans intact, so
   * `highlightMatches` still marks them). */
  text: string;
  /** Content was elided immediately before / after this slice — render an
   * ellipsis marker there. */
  elidedBefore: boolean;
  elidedAfter: boolean;
}

/** Messages up to this many characters are always shown in full — short enough
 * that a highlighted span is on screen without scrolling. */
const COLLAPSE_THRESHOLD = 600;
/** Characters of surrounding context kept on each side of a flagged span when a
 * long message is collapsed. */
const CONTEXT_CHARS = 140;

// Nudge a window edge onto a nearby whitespace boundary (within `slack` chars)
// so a snippet doesn't start/end mid-word. Falls back to the raw edge.
function snapToBoundary(
  text: string,
  edge: number,
  dir: "start" | "end",
  slack = 20,
): number {
  if (dir === "start") {
    for (let i = edge; i > Math.max(0, edge - slack); i--) {
      if (/\s/.test(text[i - 1] ?? "")) return i;
    }
    return edge;
  }
  for (let i = edge; i < Math.min(text.length, edge + slack); i++) {
    if (/\s/.test(text[i] ?? "")) return i;
  }
  return edge;
}

/** For a long flagged message, keep only a `CONTEXT_CHARS` window around each
 * matched span and collapse the rest, so the flagged value is visible without
 * scrolling. Returns `null` when nothing should be collapsed — the message is
 * short, has no in-text span to anchor on, or the windows already cover
 * (nearly) all of it. */
export function collapseToMatchWindows(
  text: string,
  matches: string[],
  context = CONTEXT_CHARS,
): MessageSnippet[] | null {
  if (text.length <= COLLAPSE_THRESHOLD) return null;
  const ranges = matchRanges(text, matches);
  if (ranges.length === 0) return null;

  const windows: Array<[number, number]> = [];
  for (const [start, end] of ranges) {
    const from = snapToBoundary(text, Math.max(0, start - context), "start");
    const to = snapToBoundary(
      text,
      Math.min(text.length, end + context),
      "end",
    );
    const last = windows[windows.length - 1];
    if (last && from <= last[1]) last[1] = Math.max(last[1], to);
    else windows.push([from, to]);
  }

  // Only collapse when it meaningfully shortens the message; otherwise the
  // ellipsis markers add noise for little gain.
  const kept = windows.reduce((sum, [from, to]) => sum + (to - from), 0);
  if (text.length - kept < context) return null;

  return windows.map(([from, to]) => ({
    text: text.slice(from, to),
    elidedBefore: from > 0,
    elidedAfter: to < text.length,
  }));
}

/** Wrap every occurrence of `matches` in `text` with a yellow highlight. When
 * `masked`, the matched characters are dotted out (the surrounding context
 * stays visible). With `maskWhole` the entire text is the sensitive value
 * (see `needsWholeMessageMask`): it is dotted out as one block while masked
 * and keeps the sensitive styling once revealed, any spans marked within. */
export function highlightMatches(
  text: string,
  matches: string[],
  masked: boolean,
  /** The spans are maskable (sensitive), so render them with the fixed-width
   * style in both states even when currently revealed — avoids reflow on toggle. */
  maskable = false,
  maskWhole = false,
): ReactNode {
  if (maskWhole) {
    return (
      <span
        className={cn(
          SENSITIVE_MARK_BASE,
          masked ? SENSITIVE_MARK_MASKED : SENSITIVE_MARK_REVEALED,
        )}
      >
        {masked
          ? maskBlock(text)
          : highlightMatches(text, matches, false, maskable)}
      </span>
    );
  }
  if (matches.length === 0) return text;

  const merged = matchRanges(text, matches);
  if (merged.length === 0) return text;

  const nodes: ReactNode[] = [];
  let pos = 0;
  merged.forEach(([start, end], k) => {
    if (start > pos) nodes.push(text.slice(pos, start));
    const value = text.slice(start, end);
    const className = maskable
      ? cn(
          SENSITIVE_MARK_BASE,
          masked ? SENSITIVE_MARK_MASKED : SENSITIVE_MARK_REVEALED,
        )
      : RISK_MARK_CLASS;
    nodes.push(
      <mark key={k} className={className}>
        {masked ? maskValue(value) : value}
      </mark>,
    );
    pos = end;
  });
  if (pos < text.length) nodes.push(text.slice(pos));
  return nodes;
}
